/**
 * HTTP client singleton untuk seluruh frontend.
 *
 * Pengganti pola lama:
 *   - `import axios from "axios"` + `${api_url}/...` manual
 *   - `ApiService()` factory yang baca token saat module load
 *
 * Fitur:
 *   - baseURL otomatis dari env
 *   - request interceptor: inject token dari localStorage per-request (selalu fresh)
 *   - response interceptor: auto-redirect ke login saat 401
 *   - dipakai oleh seluruh composable & fetcher di src/queries/api/
 */
import axios from "axios";

const TOKEN_KEY = "token-auth-jaskipin";
const REFRESH_KEY = "refresh-token-jaskipin";

function readToken() {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && parsed.token ? parsed.token : null;
  } catch (_) {
    return null;
  }
}

function readRefreshToken() {
  try {
    const raw = localStorage.getItem(REFRESH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeTokens({ token, refresh_token }) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, JSON.stringify({ token }));
  }
  if (refresh_token) {
    localStorage.setItem(REFRESH_KEY, JSON.stringify(refresh_token));
  }
}

export function clearAuthTokens() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem("data-profile-jaskipin");
  } catch (_) {
    /* ignore */
  }
}

const http = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 60 * 1000,
});

http.interceptors.request.use(
  (config) => {
    const token = readToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.token = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* ============================================================
 * In-flight request deduplication
 * ============================================================
 *
 * Tujuan: bila beberapa method di mounted()/created() panggil endpoint sama
 * secara paralel, hanya 1 request yang benar-benar ke BE. Sisanya share promise.
 *
 * Hanya berlaku untuk method idempotent (GET, HEAD, OPTIONS).
 * Mutation (POST/PUT/DELETE) selalu di-fire fresh.
 *
 * Key didasarkan dari method + url (absolute) + params (sorted).
 *
 * Pengecualian (skip dedupe):
 *   - Request dengan `config.skipDedupe = true`
 *   - Response type non-JSON (blob, arraybuffer) — biasanya download file
 */

const inFlight = new Map();

function stableStringify(obj) {
  if (obj === null || obj === undefined) return "";
  if (typeof obj !== "object") return String(obj);
  const keys = Object.keys(obj).sort();
  const parts = [];
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined) continue;
    parts.push(k + "=" + (typeof v === "object" ? JSON.stringify(v) : String(v)));
  }
  return parts.join("&");
}

function buildDedupeKey(config) {
  const method = (config.method || "get").toLowerCase();
  const url = config.baseURL ? config.baseURL + config.url : config.url;
  const params = stableStringify(config.params || {});
  return method + ":" + url + "?" + params;
}

function shouldDedupe(config) {
  if (config.skipDedupe) return false;
  if (config.responseType && config.responseType !== "json") return false;
  const method = (config.method || "get").toLowerCase();
  return method === "get" || method === "head" || method === "options";
}

/* Adapter wrapper: kalau key sama in-flight, return promise yang ada. */
const originalAdapter = http.defaults.adapter;
http.defaults.adapter = function (config) {
  if (!shouldDedupe(config)) {
    return originalAdapter(config);
  }
  const key = buildDedupeKey(config);
  const existing = inFlight.get(key);
  if (existing) {
    return existing;
  }
  const promise = originalAdapter(config).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
};

/* ============================================================
 * Auto-refresh access token saat 401
 * ============================================================
 *
 * Flow:
 *   1. Request → server return 401 (access expired / invalid)
 *   2. Interceptor cek code === "TOKEN_EXPIRED" + ada refresh_token
 *   3. Panggil POST /auth/refresh dengan refresh_token
 *   4. Simpan access + refresh baru, retry request gagal sekali
 *   5. Kalau refresh juga gagal → clear auth + redirect /login
 *
 * Multiple concurrent 401: pakai single in-flight refresh promise supaya
 * tidak race-call /refresh berkali-kali.
 */
let refreshPromise = null;

function callRefresh(refreshToken) {
  if (refreshPromise) return refreshPromise;
  refreshPromise = axios
    .post(
      `${process.env.VUE_APP_API_URL}/api/v1/auth/refresh`,
      { refresh_token: refreshToken }
    )
    .then((res) => res.data)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function redirectToLogin() {
  if (
    typeof window !== "undefined" &&
    window.location &&
    !window.location.pathname.includes("/login") &&
    window.location.pathname !== "/"
  ) {
    window.location.assign("/");
  }
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error && error.config;
    const status = error && error.response && error.response.status;
    const code = error && error.response && error.response.data && error.response.data.code;

    /* Skip kalau request adalah /refresh atau /login sendiri, atau sudah retry */
    const url = (originalRequest && originalRequest.url) || "";
    const isAuthFlow = url.includes("/auth/refresh") || url.includes("/auth/login");

    if (status === 401 && !isAuthFlow && originalRequest && !originalRequest._retry) {
      const refreshToken = readRefreshToken();
      if (refreshToken) {
        originalRequest._retry = true;
        try {
          const data = await callRefresh(refreshToken);
          if (data && data.token) {
            writeTokens(data);
            /* Retry request awal dengan token baru. */
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.token = data.token;
            return http(originalRequest);
          }
        } catch (_) {
          /* fall through ke logout */
        }
      }
      /* Refresh gagal / tidak ada — logout & redirect. */
      clearAuthTokens();
      if (code !== "TOKEN_EXPIRED") {
        /* hanya redirect kalau benar-benar unauthorized, bukan saat user
         * baru lewat /login dengan token expired (handled di atas) */
      }
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export default http;
